import { ViewIds } from '@ardoq/api-types';
import { isScenarioMode } from 'models/utils/scenarioUtils';
import {
  type FocusedChrome,
  focusedChromeOperations,
} from 'scope/focusedChromeOperations';
import { VIEW_SUPPORTING_DIFF } from 'scope/consts';
import { isInScopeDiffMode } from 'scope/scopeDiff';
import { MetaInfo, MetaInfoViewStatus } from 'streams/views/mainContent/types';
import { ActiveTabsAndTabOptions, Tab } from './types';
import {
  DEFAULT_REPLACEMENT_FOR_WORKSPACE_MODE,
  replaceViewIfDiscontinued,
} from 'views/environmentals/restrictedViews';
import { isWorkspaceModeView } from 'views/environmentals/viewSettings/utils';
import type { ModeViews } from 'views/modeViews$';
import { getRecentlyUsedStartView } from './startViewFallback';

export const setActiveTab = (tabs: Tab[], activeTabId: ViewIds) =>
  tabs.map(tab => ({
    ...tab,
    isActive: activeTabId === tab.tabId,
  }));

export const getRightTabId = (tabs: Tab[], activeTabIdLeft: ViewIds) =>
  tabs.length === 0
    ? ViewIds.NONE
    : (tabs.find(({ id }) => id !== activeTabIdLeft) ?? tabs[0]).id;

export const isDisabledTab = (viewId: ViewIds) => {
  if (isScenarioMode() && isInScopeDiffMode()) {
    return !VIEW_SUPPORTING_DIFF.includes(viewId);
  }
  return false;
};

export const metaInfoListToTabs = (metaInfoList: MetaInfo[]) =>
  metaInfoList.map(({ id, name, viewStatus = MetaInfoViewStatus.NONE }) => ({
    id,
    name: name ?? (id as string),
    label: name ?? (id as string),
    tabId: id,
    viewStatus,
    isDisabled: isDisabledTab(id),
  }));

export const canDisplayTab = (
  { id: tabId, viewStatus }: MetaInfo,
  activeTabIds: ViewIds[],
  viewsAvailableInScope: Set<ViewIds>
) => {
  if (tabId === ViewIds.NONE) {
    return false;
  }
  if (activeTabIds.includes(tabId)) {
    return true;
  }
  if (viewStatus === MetaInfoViewStatus.DISCONTINUED) {
    return false;
  }
  return viewsAvailableInScope.has(tabId);
};

type NeedsToBeSavedArgs = {
  activeViews: ViewIds[];
  viewsAvailableInScope: Set<ViewIds>;
  isPresentationMode: boolean;
  isHardNaxEnabled: boolean;
};

/**
 * Whether the active views should be written back onto the workspace. A Hard
 * NAX org never rewrites workspace views because the focused-chrome shapes are
 * display-only and its non-visualization modules are never focused.
 */
export const getNeedsToBeSaved = ({
  activeViews,
  viewsAvailableInScope,
  isPresentationMode,
  isHardNaxEnabled,
}: NeedsToBeSavedArgs): boolean =>
  !isHardNaxEnabled &&
  !isPresentationMode &&
  !activeViews.every(id => viewsAvailableInScope.has(id));

type OpenedWorkspaceStartViewArgs = {
  focusedChrome: FocusedChrome;
  hasContextPresentation: boolean;
  workspaceStartView: ViewIds | undefined;
  recentlyUsedViewStyle: ViewIds | null;
  defaultView: ViewIds;
  availableViewIds: ViewIds[];
  deprecatedViews: MetaInfo[];
  workspaceModeViewsById: ModeViews<ViewIds>['byIds'];
};

/**
 * The view to open on when a workspace or scenario is loaded rather than
 * switched to. Focused chrome narrows the views on offer, so the workspace's
 * own start view does not apply and the chrome names its own.
 */
export const getOpenedWorkspaceStartView = ({
  focusedChrome,
  hasContextPresentation,
  workspaceStartView,
  recentlyUsedViewStyle,
  defaultView,
  availableViewIds,
  deprecatedViews,
  workspaceModeViewsById,
}: OpenedWorkspaceStartViewArgs): ViewIds | null => {
  if (focusedChromeOperations.isFocusedChrome(focusedChrome)) {
    return focusedChromeOperations.getStartViewId(focusedChrome);
  }
  if (hasContextPresentation) {
    return null;
  }
  const recentlyUsedStartView = getRecentlyUsedStartView({
    workspaceStartView: workspaceStartView ?? null,
    recentlyUsedViewStyle,
    workspaceModeViewsById,
  });
  const startView =
    replaceViewIfDiscontinued(
      workspaceStartView ?? recentlyUsedStartView ?? defaultView,
      deprecatedViews
    ) ?? defaultView;
  return availableViewIds.includes(startView) ? startView : defaultView;
};

type SantitzeActiveTabsArgs = {
  requestedTabIdLeft: ViewIds;
  requestedTabIdRight: ViewIds;
  isScenarioMode: boolean;
  focusedChrome: FocusedChrome;
  state: ActiveTabsAndTabOptions;
  /** The user's workspace mode views. */
  availableViewIds: ViewIds[];
  isRestrictedToQuickStartViews: boolean;
};

/**
 * A slide or link can name a view workspace mode does not have. Interactive
 * Blocks, once a workspace view, and the viewpoint block diagrams open the Block
 * Diagram; any other opens Pages rather than an empty pane. So does a view quick
 * start hides from the user.
 */
const replaceViewOutsideWorkspaceMode = (
  viewId: ViewIds,
  availableViewIds: ViewIds[],
  isRestrictedToQuickStartViews: boolean
): ViewIds => {
  if (!viewId) {
    return viewId;
  }
  if (isWorkspaceModeView(viewId)) {
    return isRestrictedToQuickStartViews && !availableViewIds.includes(viewId)
      ? DEFAULT_REPLACEMENT_FOR_WORKSPACE_MODE
      : viewId;
  }
  if (
    viewId === ViewIds.BLOCKS ||
    viewId === ViewIds.MODERNIZED_BLOCK_DIAGRAM ||
    viewId === ViewIds.FLEXIBLE_BLOCK_DIAGRAM
  ) {
    return ViewIds.BLOCK_DIAGRAM;
  }
  return DEFAULT_REPLACEMENT_FOR_WORKSPACE_MODE;
};

export const sanitizeActiveTabs = ({
  requestedTabIdLeft,
  requestedTabIdRight,
  isScenarioMode,
  focusedChrome,
  state,
  availableViewIds,
  isRestrictedToQuickStartViews,
}: SantitzeActiveTabsArgs) => {
  const workspaceTabIdLeft = replaceViewOutsideWorkspaceMode(
    requestedTabIdLeft,
    availableViewIds,
    isRestrictedToQuickStartViews
  );
  const workspaceTabIdRight = replaceViewOutsideWorkspaceMode(
    requestedTabIdRight,
    availableViewIds,
    isRestrictedToQuickStartViews
  );
  const conformTabLeft =
    isDisabledTab(workspaceTabIdLeft) || (!workspaceTabIdLeft && isScenarioMode)
      ? state.activeTabIdLeft || state.previousActiveTabIdLeft
      : workspaceTabIdLeft;
  const conformTabRight = isDisabledTab(workspaceTabIdRight)
    ? state.activeTabIdRight
    : workspaceTabIdRight;

  if (!focusedChromeOperations.isFocusedChrome(focusedChrome)) {
    return {
      activeTabIdLeft: conformTabLeft,
      activeTabIdRight: conformTabRight,
    };
  }

  return {
    activeTabIdLeft: focusedChromeOperations.replaceViewIfNotAllowed(
      focusedChrome,
      conformTabLeft
    ),
    activeTabIdRight: ViewIds.NONE,
  };
};
